import { CircleAlertIcon } from 'lucide-react';
import * as React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Minimal form field helpers (label + control + description + error) that work with
 * react-hook-form's `register` without a Form context.
 */

interface FieldProps extends React.ComponentProps<'div'> {
  label?: React.ReactNode;
  htmlFor?: string;
  description?: React.ReactNode;
  error?: string | undefined;
  required?: boolean;
  /** Rendered on the right of the label row, e.g. a "Forgot password?" link. */
  labelAction?: React.ReactNode;
}

function Field({ label, htmlFor, description, error, required, labelAction, className, children, ...props }: FieldProps) {
  return (
    <div data-slot="field" className={cn('grid gap-2', className)} {...props}>
      {label && (
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor={htmlFor} className={cn(error && 'text-destructive')}>
            {label}
            {required && <span className="text-muted-foreground font-normal">*</span>}
          </Label>
          {labelAction}
        </div>
      )}
      {children}
      {description && !error && <p className="text-muted-foreground text-xs">{description}</p>}
      {error && (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
    </div>
  );
}

function FormError({ message, className }: { message?: string | null; className?: string }) {
  if (!message) return null;
  return (
    <div role="alert" className={cn('border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2.5 border px-3 py-2.5 text-sm', className)}>
      <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export { Field, FormError };
